#!/usr/bin/env node

import { Command } from "commander";
import chalk from "chalk";
import readline from "readline";
import { execSync, spawnSync } from "node:child_process";
import { readFileSync, writeFileSync, mkdirSync, existsSync } from "node:fs";
import { join } from "node:path";
import { homedir } from "node:os";
import { monitorCommand } from "./commands/monitor.js";
import {
  watchdogBriefCommand,
  watchdogListCommand,
  watchdogMarkCommand,
  watchdogPathCommand,
  watchdogShowCommand,
} from "./commands/watchdog.js";
import { parseFailOn, scanCommand, type ScanFormat } from "./commands/scan.js";
import { initCommand } from "./commands/init.js";
import { statusCommand } from "./commands/status.js";
import { modulesCommand } from "./commands/modules.js";
import { pentestCommand } from "./commands/pentest.js";
import { aiScanCommand, aiScanMoshCommand } from "./commands/ai-scan.js";
import { orgsCommand } from "./commands/orgs.js";
import { serversCommand } from "./commands/servers.js";
import { connectCommand } from "./commands/connect.js";
import { daemonForeground, daemonRestart, daemonStart, daemonStop } from "./commands/daemon.js";
import { installServiceCommand, uninstallServiceCommand } from "./commands/service.js";
import { loginCommand, logoutCommand, whoamiCommand } from "./commands/login.js";
import { saveCommand } from "./commands/save.js";
import { welcomeCommand } from "./commands/welcome.js";
import {
  propertiesAddCommand,
  propertiesImportCommand,
  propertiesListCommand,
  propertiesRemoveCommand,
  propertiesRunCommand,
  propertiesRunsCommand,
} from "./commands/properties.js";
import { rulesCommand } from "./commands/rules.js";
import { hardenCommand } from "./commands/harden.js";
import { patchesCommand } from "./commands/patches.js";
import { feedsCommand } from "./commands/feeds.js";
import { reputationLookupCommand, reputationSubscribeCommand, reputationWatchCommand } from "./commands/reputation.js";
import { privilegedCommand } from "./commands/privileged.js";
import { blockCommand, unblockCommand, blocklistCommand, allowlistCommand } from "./commands/firewall.js";
import {
  abuseHistoryCommand,
  abusePendingCommand,
  abuseRetractCommand,
  abuseStatusCommand,
} from "./commands/abuse.js";
import { PATHS } from "./daemon/paths.js";
import {
  DAEMON_UNIT,
  activeCliPath,
  activeCliVersion,
  daemonRefreshPlan,
  globalHolders,
  globalInstallCommand,
  globalListCommand,
  latestPublishedVersion,
  shadowedUpdateWarning,
  staleDaemonWarning,
  systemdOwnsDaemon,
  type PackageManager,
} from "./upgrade.js";
import { IpcClient } from "./core/ipc-client.js";
import { findRunningDaemon } from "./daemon/pidfile.js";
import { PKG_VERSION } from "./core/version.js";

const LOGO = `
${chalk.green("  ████████╗██╗  ██╗██████╗ ███████╗ █████╗ ████████╗")}
${chalk.green("  ╚══██╔══╝██║  ██║██╔══██╗██╔════╝██╔══██╗╚══██╔══╝")}
${chalk.green("     ██║   ███████║██████╔╝█████╗  ███████║   ██║   ")}
${chalk.green("     ██║   ██╔══██║██╔══██╗██╔══╝  ██╔══██║   ██║   ")}
${chalk.green("     ██║   ██║  ██║██║  ██║███████╗██║  ██║   ██║   ")}
${chalk.green("     ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝")}
${chalk.dim("                    C R U S H")}
`;

const API_URL = process.env.THREATCRUSH_API_URL || "https://threatcrush.com";
const PKG_NAME = "@profullstack/threatcrush";
const DESKTOP_PKG_NAME = "@profullstack/threatcrush-desktop";
const INSTALL_CONFIG_PATH = join(homedir(), ".threatcrush", "install.json");

// ─── Helpers ───

async function joinWaitlist(email: string): Promise<{ referral_code?: string } | null> {
  try {
    const res = await fetch(`${API_URL}/api/waitlist`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return await res.json();
  } catch {
    return null;
  }
}

function detectPackageManager(): PackageManager {
  // Check what installed us
  try {
    const npmGlobal = execSync("npm ls -g --depth=0 --json 2>/dev/null", { encoding: "utf-8" });
    if (npmGlobal.includes(PKG_NAME)) return "npm";
  } catch {}
  try {
    execSync("pnpm --version", { stdio: "pipe" });
    return "pnpm";
  } catch {}
  try {
    execSync("yarn --version", { stdio: "pipe" });
    return "yarn";
  } catch {}
  try {
    execSync("bun --version", { stdio: "pipe" });
    return "bun";
  } catch {}
  return "npm";
}

function readInstallConfig(): { installMode?: string; packageManager?: string; installMethod?: string } {
  try {
    return JSON.parse(readFileSync(INSTALL_CONFIG_PATH, "utf-8"));
  } catch {
    return {};
  }
}

function packageLooksInstalled(pm: PackageManager, pkgName: string): boolean {
  try {
    const output = execSync(globalListCommand(pm, pkgName), {
      encoding: "utf-8",
      stdio: ["pipe", "pipe", "pipe"],
    });

    return output.includes(pkgName);
  } catch {
    return false;
  }
}

/**
 * Runs one upgrade command per package manager that holds a global copy, so a
 * second install under another package manager cannot keep shadowing the new
 * one. Falls back to whichever manager we detect when nothing reports a copy.
 */
function upgradeCommands(pkgName: string): string[] {
  const holders = globalHolders(pkgName);
  const targets = holders.length > 0 ? holders : [detectPackageManager()];
  return targets.map((pm) => globalInstallCommand(pm, pkgName, "update"));
}

/**
 * A clean install still leaves the user on the old build when PATH resolves
 * somewhere else, and the old "✓ updated successfully" said nothing about it.
 */
async function reportIfStillStale(): Promise<void> {
  const warning = shadowedUpdateWarning(
    await latestPublishedVersion(PKG_NAME),
    activeCliVersion(),
    activeCliPath(),
  );
  if (!warning) return;

  console.log(chalk.yellow(`  ${warning[0]}`));
  for (const line of warning.slice(1)) {
    console.log(chalk.dim(`  ${line}`));
  }
  console.log();
}

/** The running daemon's pid and version, or nulls when it is not up. */
async function liveDaemon(): Promise<{ pid: number | null; version: string | null }> {
  const pid = findRunningDaemon();
  if (!pid) return { pid: null, version: null };

  const client = new IpcClient();
  try {
    await client.connect();
    const live = await client.status();
    return { pid, version: live.version ?? null };
  } catch {
    // Daemon is up but not answering IPC — we still know it is running.
    return { pid, version: null };
  } finally {
    client.close();
  }
}

/**
 * Bring the running daemon onto the build we just installed.
 *
 * The CLI upgrade replaces files on disk and nothing else, so without this the
 * daemon serves the old version indefinitely while `update` reports success.
 */
async function refreshDaemonAfterUpgrade(): Promise<void> {
  const before = await liveDaemon();
  const plan = daemonRefreshPlan({
    daemonRunning: before.pid !== null,
    systemdManaged: systemdOwnsDaemon(
      execQuiet(`systemctl show ${DAEMON_UNIT} -p MainPID --value`),
      before.pid,
    ),
  });

  if (plan.action === "none") return;

  if (plan.action === "manual") {
    // A systemd-managed daemon must be restarted *through* systemd, or we swap
    // a supervised daemon for an unsupervised one. That is a reason to use
    // systemctl — not a reason to print the command and leave the daemon
    // running the old build until somebody gets around to it.
    console.log(chalk.dim("  Restarting the systemd service onto the new build...\n"));
    const restarted = restartSystemdDaemon();
    if (!restarted) {
      console.log(chalk.yellow("  ! Could not restart the service; it is still on the old build."));
      console.log(chalk.dim(`    Restart it with:  ${plan.command}\n`));
      return;
    }

    const after = await liveDaemon();
    const stale = staleDaemonWarning(PKG_VERSION, after.version);
    if (stale) {
      console.log(chalk.yellow(`  ${stale[0]}`));
      for (const line of stale.slice(1)) console.log(chalk.dim(`  ${line}`));
      console.log();
    } else {
      console.log(chalk.green(`  ✓ threatcrushd restarted on ${after.version ?? PKG_VERSION}.\n`));
    }
    return;
  }

  console.log(chalk.dim("  Restarting threatcrushd onto the new build...\n"));
  await daemonRestart();

  const after = await liveDaemon();
  const warning = staleDaemonWarning(PKG_VERSION, after.version);
  if (warning) {
    console.log(chalk.yellow(`  ${warning[0]}`));
    for (const line of warning.slice(1)) {
      console.log(chalk.dim(`  ${line}`));
    }
    console.log();
  }
}

/**
 * A daemon that means to ban and cannot is a broken install, not a preference.
 *
 * Auto-defence enforces by default, and an unprivileged daemon detects a
 * firewall backend happily (`nft --version` succeeds for anybody) while being
 * unable to write a single rule. Rather than reporting that and leaving the
 * host undefended until somebody reads the line, repair it: installing the
 * system service is exactly the fix, and it asks for root itself.
 *
 * Only runs when the daemon says it intends to enforce and cannot — never on a
 * daemon that is disabled, in dry-run, or already privileged.
 */
async function promoteUnprivilegedDaemon(): Promise<void> {
  const client = new IpcClient();
  let warning: string | undefined;
  try {
    await client.connect();
    warning = (await client.blocklist()).warning;
  } catch {
    // Older daemon, or none running. Nothing to repair from here.
    return;
  } finally {
    client.close();
  }

  if (!warning) return;

  console.log(chalk.yellow(`  ! ${warning}`));
  console.log(chalk.dim("  Auto-defence is on, so installing the system service to give it that privilege.\n"));
  await installServiceCommand();
}

/**
 * Restart the systemd unit, elevating if we are not root.
 *
 * sudo's prompt goes straight to the operator's terminal, so this asks for the
 * privilege at the moment it is needed instead of handing back a command.
 */
function restartSystemdDaemon(): boolean {
  const asRoot = typeof process.getuid === "function" && process.getuid() === 0;
  const argv = asRoot
    ? ["systemctl", "restart", DAEMON_UNIT]
    : ["sudo", "systemctl", "restart", DAEMON_UNIT];

  if (!asRoot && spawnSync("sudo", ["--version"], { stdio: "pipe" }).status !== 0) return false;

  const [cmd, ...args] = argv;
  if (spawnSync(cmd, args, { stdio: "inherit" }).status !== 0) return false;

  // `systemctl restart` returns before the unit is necessarily up.
  return spawnSync("systemctl", ["is-active", "--quiet", DAEMON_UNIT], { stdio: "pipe" }).status === 0;
}

/** Runs a command for its stdout, swallowing failure and a missing binary. */
function execQuiet(cmd: string): string | null {
  try {
    return execSync(cmd, { encoding: "utf-8", stdio: ["pipe", "pipe", "pipe"] });
  } catch {
    return null;
  }
}

// ─── Program ───

const program = new Command();

program
  .name("threatcrush")
  .description(
    `${chalk.green("⚡ ThreatCrush")} — All-in-one security agent

  Read your logs and watch inbound connections for live attacks,
  scan your code, spot-check your URLs, and alert you in real-time.

  ${chalk.dim("Website:")}  ${chalk.green("https://threatcrush.com")}
  ${chalk.dim("GitHub:")}   ${chalk.green("https://github.com/profullstack/threatcrush")}
  ${chalk.dim("npm:")}      ${chalk.green("https://www.npmjs.com/package/@profullstack/threatcrush")}
  ${chalk.dim("Pricing:")}  Contact us for a quote: ${chalk.green("https://threatcrush.com/hire")}

${chalk.dim("Examples:")}
  ${chalk.green("$")} threatcrush monitor          ${chalk.dim("# Real-time monitoring")}
  ${chalk.green("$")} threatcrush tui              ${chalk.dim("# Interactive dashboard")}
  ${chalk.green("$")} threatcrush map              ${chalk.dim("# Live map of where attacks come from")}
  ${chalk.green("$")} threatcrush watchdog         ${chalk.dim("# Triage logged attacks: hole or not?")}
  ${chalk.green("$")} threatcrush scan ./src       ${chalk.dim("# Scan code for vulns")}
  ${chalk.green("$")} threatcrush pentest URL      ${chalk.dim("# Quick web checks on a URL")}
  ${chalk.green("$")} threatcrush ai-scan URL      ${chalk.dim("# Red-team your AI/LLM endpoint (OWASP LLM Top 10)")}
  ${chalk.green("$")} threatcrush modules install  ${chalk.dim("# Install a module")}
  ${chalk.green("$")} threatcrush update           ${chalk.dim("# Update to latest")}
  ${chalk.green("$")} threatcrush remove           ${chalk.dim("# Uninstall completely")}`)
  .version(PKG_VERSION, "-v, --version", "Show version number")
  .helpOption("-h, --help", "Show this help")
  .addHelpText("after", `
${chalk.dim("─────────────────────────────────────────────────────")}
${chalk.dim("Modules:")}
  ThreatCrush uses pluggable security modules. Built in:
  ${chalk.green("log-watcher")}      nginx access log + syslog, OWASP CRS rules
  ${chalk.green("ssh-guard")}        Failed logins, brute force, root logins, user enumeration
  ${chalk.green("user-journal")}     journald
  ${chalk.green("network-monitor")}  Inbound connections (conntrack/ss): port scans, SYN floods
  ${chalk.green("dns-monitor")}      DNS tunneling, DGA detection
  Plus: ${chalk.green("scan")} (code, secrets, dependency CVEs), ${chalk.green("pentest")} (web checks),
  auto-defend (fail2ban/nftables/iptables) and alerts (Slack, Discord, email, webhook, PagerDuty).

  Browse community modules: ${chalk.green("threatcrush store")}
`);

// ─── Real commands ───

program
  .command("monitor")
  .description("Watch nginx, auth and syslog for attacks in the foreground")
  .option("-m, --module <modules>", "Comma-separated module filter (e.g. ssh-guard,log-watcher)")
  .option("--tui", "Launch the interactive TUI dashboard")
  .option("--demo", "Run the dashboard on canned events (screenshots, no daemon)")
  .action(async (opts) => {
    await monitorCommand({
      module: opts.module,
      tui: opts.tui,
      demo: opts.demo,
    });
  });

program
  .command("tui")
  .description("Interactive security dashboard (htop for security)")
  .alias("dashboard")
  .option("--demo", "Run on canned events instead of connecting to the daemon")
  .action(async (opts) => {
    await monitorCommand({ tui: true, demo: opts.demo });
  });

program
  .command("map")
  .description("Live world map of where attacks on this server come from")
  .option("--demo", "Show canned attacks instead of connecting to the daemon")
  .addHelpText("after", `
Addresses from the daemon's live feed are placed with threatcrush.com/api/geo
(DB-IP City Lite); this server is placed by the address it reaches us from.
Drawn in braille everywhere, and as an HD image in hqterm, iTerm2, WezTerm,
Kitty and Ghostty (over SSH or tmux set HQTUI_IMAGES=iterm or =1; turn it off
with THREATCRUSH_MAP_IMAGES=0). Press m to switch to the dashboard panels.`)
  .action(async (opts) => {
    const { startDashboard } = await import("./tui/dashboard.js");
    await startDashboard({ demo: opts.demo, view: "map" });
  });

// ─── Watchdog: triage the attacks the dashboard logged ───

const watchdogCmd = program
  .command("watchdog")
  .description("Triage the attacks the daemon logged: was it a hole? fix it, PR it, mark it")
  .option("--dir <dir>", "Watchdog log directory")
  .action((opts) => {
    watchdogListCommand(opts);
  });

watchdogCmd
  .command("list")
  .alias("ls")
  .description("Findings to triage, worst first (answered 2xx, then 5xx, ...)")
  .option("--status <status>", "Only this triage status: new | hole | fp | fixed")
  .option("--outcome <outcome>", "Only this outcome: answered | errored | unknown | redirected | refused")
  .option("-a, --all", "Include refused and already-triaged findings")
  .option("-n, --limit <n>", "Show at most n findings", "50")
  .option("--json", "Output findings as JSON")
  .action((_opts, cmd) => {
    watchdogListCommand(cmd.optsWithGlobals());
  });

watchdogCmd
  .command("show <id>")
  .description("Every detail of one finding: samples, sources, a repro command")
  .option("--json", "Output as JSON")
  .action((id: string, _opts, cmd) => {
    watchdogShowCommand(id, cmd.optsWithGlobals());
  });

watchdogCmd
  .command("brief <id>")
  .description("Markdown hand-off to verify and fix one finding (paste into an issue, PR or agent)")
  .action((id: string, _opts, cmd) => {
    watchdogBriefCommand(id, cmd.optsWithGlobals());
  });

watchdogCmd
  .command("mark <id> <status>")
  .description("Record a verdict: hole | fp | fixed | new")
  .option("--note <text>", "Why: what you checked, what you found")
  .option("--pr <url>", "The PR that fixes it")
  .action((id: string, status: string, _opts, cmd) => {
    watchdogMarkCommand(id, status, cmd.optsWithGlobals());
  });

watchdogCmd
  .command("path")
  .description("Print the watchdog log directory")
  .action((_opts, cmd) => {
    watchdogPathCommand(cmd.optsWithGlobals());
  });

const abuseCmd = program
  .command("abuse")
  .description("Report confirmed attack sources to shared feeds (AbuseIPDB), and see what was reported")
  .option("--dir <dir>", "Abuse-report audit directory")
  .action((_opts, cmd) => {
    abuseStatusCommand(cmd.optsWithGlobals());
  });

abuseCmd
  .command("status")
  .description("Whether reporting is active, the evidence bar, and the counts")
  .action((_opts, cmd) => {
    abuseStatusCommand(cmd.optsWithGlobals());
  });

abuseCmd
  .command("history")
  .alias("list")
  .alias("ls")
  .description("Everything reported (or would-be, in dry-run), newest first")
  .option("--json", "Output as JSON")
  .action((_opts, cmd) => {
    abuseHistoryCommand(cmd.optsWithGlobals());
  });

abuseCmd
  .command("pending")
  .description("Sources judged reportable but not sent (dry-run), with their evidence")
  .option("--json", "Output as JSON")
  .action((_opts, cmd) => {
    abusePendingCommand(cmd.optsWithGlobals());
  });

abuseCmd
  .command("retract <id>")
  .description("Withdraw a report from our feed and record it for the upstream")
  .option("--note <text>", "Why it was wrong")
  .action((id: string, _opts, cmd) => {
    abuseRetractCommand(id, cmd.optsWithGlobals());
  });

program
  .command("init")
  .description("Sign in, auto-detect services, and configure ThreatCrush")
  .option("-y, --yes", "Don't ask; answer yes to every question")
  .option("--offline", "Skip signing in to threatcrush.com")
  .action(async (opts: { yes?: boolean; offline?: boolean }) => {
    await initCommand({ yes: opts.yes, offline: opts.offline });
  });

program
  .command("login")
  .description("Log in to your threatcrush.com account (prints a link to approve in your browser)")
  .option("-p, --password", "Log in with email and password instead of a link")
  .option("-e, --email <email>", "Email to log in with (implies --password)")
  .action(async (opts) => {
    await loginCommand({ email: opts.email, password: opts.password });
  });

program
  .command("save")
  .description("Save this machine's daemon config to its threatcrush.com server (secrets redacted)")
  .option("-c, --config <path>", "Config file to save (default: the one the daemon reads)")
  .action(async (opts: { config?: string }) => {
    await saveCommand({ config: opts.config });
  });

program
  .command("logout")
  .description("Clear stored threatcrush.com credentials")
  .action(async () => {
    await logoutCommand();
  });

program
  .command("whoami")
  .description("Show the currently logged-in threatcrush.com account")
  .action(async () => {
    await whoamiCommand();
  });

program
  .command("scan")
  .description("Scan codebase for vulnerabilities and secrets")
  .argument("[path]", "Path to scan", ".")
  .option("-f, --format <format>", "output format: text, json, or sarif", "text")
  .option("-o, --output <file>", "write json/sarif output to a file instead of stdout")
  .option(
    "--fail-on <severities>",
    "exit 1 when a finding at or above any of these exists (comma-separated: critical,high,medium,low,info)",
  )
  .option(
    "--path-prefix <prefix>",
    "prepend this to SARIF file URIs — use when the scan root is not the repository root",
  )
  .option("--deps", "also query OSV.dev for advisories against lockfile versions (network)")
  .option(
    "--exclude <glob>",
    "skip paths matching this glob (repeatable); merged with a .threatcrushignore at the scan root",
    (value: string, previous: string[]) => [...previous, value],
    [] as string[],
  )
  .option(
    "--missing-controls",
    "also report security controls the tree shows no evidence of (headers, CSRF, rate limiting, body size limits)",
  )
  .option("-v, --verbose", "list the paths that could not be read")
  .action(async (targetPath: string, opts: {
    format?: string;
    output?: string;
    failOn?: string;
    pathPrefix?: string;
    deps?: boolean;
    exclude?: string[];
    missingControls?: boolean;
    verbose?: boolean;
  }) => {
    const format = (opts.format ?? "text").toLowerCase();
    if (!["text", "json", "sarif"].includes(format)) {
      console.error(chalk.red(`Unknown --format "${opts.format}" (expected text, json, or sarif)`));
      process.exit(2);
    }

    let failOn;
    try {
      failOn = parseFailOn(opts.failOn);
    } catch (err) {
      console.error(chalk.red((err as Error).message));
      process.exit(2);
    }

    await scanCommand(targetPath, {
      format: format as ScanFormat,
      output: opts.output,
      failOn,
      pathPrefix: opts.pathPrefix,
      dependencies: opts.deps,
      exclude: opts.exclude,
      missingControls: opts.missingControls,
      verbose: opts.verbose,
    });
  });

program
  .command("pentest")
  .description("Penetration test URLs and APIs")
  .argument("<url>", "Target URL to pentest")
  .action(async (url: string) => {
    await pentestCommand(url);
  });

// ─── AI Red-Team Scan (LLM/agent endpoints you own) ───

program
  .command("ai-scan")
  .description("Red-team an AI/LLM endpoint you own for OWASP LLM Top 10 guardrail gaps")
  .argument("<url>", "Target AI endpoint URL")
  .option("--categories <list>", "comma list: prompt-injection,system-prompt-leak,sensitive-disclosure,excessive-agency,output-handling")
  .option("--template <json>", 'request body with {{PROMPT}} marker (default {"input":"{{PROMPT}}"})')
  .option("--header <k:v...>", "extra request header (repeatable)")
  .option("--response-path <path>", 'dotted JSON path to the reply text, or "text"')
  .option("--delay <ms>", "delay between probes")
  .option("--json", "emit JSON")
  .action(async (url: string, opts) => {
    await aiScanCommand(url, opts);
  });

program
  .command("ai-scan-run")
  .description("Drive the AI red-team scanner from a .mosh script")
  .argument("<file>", "Path to a .mosh script")
  .argument("[args...]", "Arguments passed to the script as argv")
  .action(async (file: string, args: string[]) => {
    await aiScanMoshCommand(file, args ?? []);
  });

program
  .command("status")
  .description("Show daemon status and loaded modules")
  .action(async () => {
    await statusCommand();
  });

// ─── Detection Rules (PRD 01) ───

program
  .command("rules")
  .description("Manage detection rules")
  .argument("[action]", "list | show", "list")
  .argument("[id]", "Rule ID (for show)")
  .action(async (action: string, id: string) => {
    await rulesCommand({ action, id });
  });

// ─── Hardening Scanner (PRD 03) ───

program
  .command("harden")
  .description("Run hardening security scan")
  .option("--json", "Output results as JSON")
  .option("--no-upload", "Do not upload findings to the dashboard (uploaded when this machine is linked)")
  .action(async (opts: { json?: boolean; upload?: boolean }) => {
    await hardenCommand(opts);
  });

program
  .command("patches")
  .description("List OS packages with a security update waiting, and the CVEs it fixes (Debian/Ubuntu)")
  .option("--json", "Output the report as JSON")
  .option("--no-upload", "Do not upload the result to the dashboard (uploaded when this machine is linked)")
  .action(async (opts: { json?: boolean; upload?: boolean }) => {
    await patchesCommand(opts);
  });

program
  .command("feeds [action] [value]")
  .description("Show the abuse.ch C2/malware list the daemon matches; `feeds check <ip|domain>` looks one up (exit 1 when listed); `feeds resolver install|remove|status` lets dnsmasq/unbound refuse the listed domains")
  .option("--json", "Output as JSON")
  .action(async (action: string | undefined, value: string | undefined, opts: { json?: boolean }) => {
    await feedsCommand(action, value, opts);
  });

const reputationCmd = program
  .command("reputation")
  .description("Look IPs or domains up against the ThreatCrush community database (exit 1 when any is listed; several at once go as one bulk request); `reputation watch` alerts on changes, `reputation subscribe` starts a Reputation Pro payment through CoinPay")
  .argument("[indicators...]", "IP addresses or domains to look up")
  .option("--json", "Output as JSON")
  .option("--org <id>", "Org to look up as (defaults to `orgs use`)")
  .action(async (indicators: string[], opts: { json?: boolean; org?: string }) => {
    if (!indicators?.length) {
      reputationCmd.help();
      return;
    }
    await reputationLookupCommand(indicators, opts);
  });

reputationCmd
  .command("watch [action] [indicator]")
  .description("Reputation Pro watch lists: `watch list`, `watch add <ip-or-domain> [--note]`, `watch rm <ip-or-domain>`; alerts go to the org's alert destinations")
  .option("--org <id>", "Org (defaults to `orgs use`)")
  .option("--note <text>", "Why you watch it, shown in the alert")
  .option("--destination <id>", "Alert destination id (default: every enabled destination)")
  .option("--json", "Output as JSON")
  .action(async (action: string | undefined, indicator: string | undefined, opts: { org?: string; note?: string; destination?: string; json?: boolean }) => {
    await reputationWatchCommand(action, indicator, opts);
  });

reputationCmd
  .command("subscribe")
  .description("Pay for Reputation Pro in crypto (history, /24 neighbours, score reasons, more lookups)")
  .option("--plan <id>", "pro-monthly or pro-yearly", "pro-monthly")
  .option("--currency <c>", "usdc_pol, usdc_sol, usdc_eth, usdt, pol, sol, btc or eth", "usdc_pol")
  .option("--org <id>", "Org to pay for (defaults to `orgs use`)")
  .option("--json", "Output as JSON")
  .action(async (opts: { plan?: string; currency?: string; org?: string; json?: boolean }) => {
    await reputationSubscribeCommand(opts);
  });

program
  .command("privileged")
  .description("Who can become root here (uid 0, sudo groups, sudoers, SSH keys) and the daemon's record of changes and sudo commands")
  .option("--json", "Output as JSON")
  .option("--limit <n>", "How many recent events to show", "25")
  .action(async (opts: { json?: boolean; limit?: string }) => {
    await privilegedCommand(opts);
  });

// ─── Firewall Commands (PRD 02) ───

program
  .command("block")
  .description("Block an IP address via the firewall")
  .argument("<ip>", "IP address to block")
  .option("--ttl <duration>", "Block duration (e.g. 1h, 30m, 1d)")
  .action(async (ip: string, opts: { ttl?: string }) => {
    await blockCommand(ip, opts);
  });

program
  .command("unblock")
  .description("Unblock an IP address")
  .argument("<ip>", "IP address to unblock")
  .action(async (ip: string) => {
    await unblockCommand(ip);
  });

program
  .command("blocklist")
  .description("Show active firewall blocklist")
  .action(async () => {
    await blocklistCommand();
  });

program
  .command("allowlist")
  .description("Manage IP allowlist")
  .argument("[action]", "list | add | remove", "list")
  .argument("[value]", "IP/CIDR to add or remove")
  .action(async (action: string, value: string) => {
    await allowlistCommand({ action, value });
  });

program
  .command("start")
  .description("Start the ThreatCrush daemon in the background")
  .action(async () => {
    console.log(LOGO);
    await daemonStart();
  });

program
  .command("stop")
  .description("Stop the ThreatCrush daemon")
  .action(async () => {
    console.log(LOGO);
    await daemonStop();
  });

program
  .command("restart")
  .description("Restart the ThreatCrush daemon (stop, then start)")
  .action(async () => {
    console.log(LOGO);
    await daemonRestart();
  });

program
  .command("daemon")
  .description("Run threatcrushd in the foreground (systemd / Docker ExecStart)")
  .action(async () => {
    await daemonForeground();
  });

program
  .command("install-service")
  .description("Install the threatcrushd systemd unit (Linux, requires sudo)")
  .action(async () => {
    await installServiceCommand();
  });

program
  .command("uninstall-service")
  .description("Remove the threatcrushd systemd unit (Linux, requires sudo)")
  .action(async () => {
    await uninstallServiceCommand();
  });

program
  .command("logs")
  .description("Tail daemon logs")
  .action(async () => {
    console.log(LOGO);
    const logPath = PATHS.logFile;
    if (!existsSync(logPath)) {
      console.log(chalk.yellow(`  No log file found at ${logPath}`));
      console.log(chalk.dim("  Start the daemon first with `threatcrush start`\n"));
      return;
    }
    console.log(chalk.green(`  Tailing ${logPath}...\n`));
    console.log(chalk.gray("  Press Ctrl+C to stop\n"));
    execSync(`tail -f ${logPath}`, { stdio: "inherit" });
  });

// ─── Real commands ───

program
  .command("update")
  .description("Update ThreatCrush CLI and installed bundle")
  .option("--cli", "Update CLI only")
  .option("--modules", "Update modules only")
  .option("--desktop", "Update desktop app too")
  .action(async (opts) => {
    console.log(LOGO);

    if (opts.modules) {
      console.log(LOGO);
      console.log(chalk.yellow("  Updating modules...\n"));
      const commands = upgradeCommands(PKG_NAME);
      try {
        for (const cmd of commands) {
          console.log(chalk.green(`  → ${cmd}\n`));
          execSync(cmd, { stdio: "inherit" });
        }
        console.log(chalk.green("\n  ✓ Modules updated successfully!\n"));
        await reportIfStillStale();
      } catch (err) {
        console.log(chalk.red("\n  ✗ Module update failed. Try manually:\n"));
        for (const cmd of commands) {
          console.log(chalk.dim(`    ${cmd}`));
        }
        console.log();
      }
      return;
    }

    const holders = globalHolders(PKG_NAME);
    const installConfig = readInstallConfig();
    const installMode = opts.cli ? "server" : (opts.desktop ? "desktop" : installConfig.installMode || "server");

    console.log(chalk.dim(`  Detected package manager: ${holders.join(", ") || detectPackageManager()}`));
    console.log(chalk.dim(`  Install mode: ${installMode}`));
    console.log(chalk.dim(`  Current version: ${PKG_VERSION}\n`));

    // The desktop app ships as a GitHub Releases bundle, not an npm package,
    // so there is nothing to update globally for it here.
    const commands = upgradeCommands(PKG_NAME);

    try {
      for (const cmd of commands) {
        console.log(chalk.green(`  → ${cmd}\n`));
        execSync(cmd, { stdio: "inherit" });
      }

      console.log(chalk.green("\n  ✓ ThreatCrush updated successfully!\n"));
      if (installMode === "desktop") {
        console.log(chalk.dim("  Updated bundle: CLI + desktop app\n"));
      } else {
        console.log(chalk.dim("  Updated bundle: CLI only\n"));
      }
      await reportIfStillStale();
      await refreshDaemonAfterUpgrade();
      // An update is also the moment to notice the daemon cannot do the job it
      // was configured for, and fix it rather than describe it.
      await promoteUnprivilegedDaemon();
      // A running `monitor --tui` loaded its code at launch, so it keeps
      // rendering the old build however current everything on disk is.
      console.log(chalk.dim("  If a `threatcrush monitor --tui` is open, quit and relaunch it.\n"));
    } catch (err) {
      console.log(chalk.red("\n  ✗ Update failed. Try manually:\n"));
      for (const cmd of commands) {
        console.log(chalk.dim(`    ${cmd}`));
      }
      console.log();
    }
  });

program
  .command("remove")
  .description("Uninstall ThreatCrush and the installed bundle")
  .alias("uninstall")
  .action(async () => {
    console.log(LOGO);

    const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
    const confirm = await new Promise<string>((resolve) => {
      rl.question(chalk.yellow("  Are you sure you want to uninstall ThreatCrush? (y/N): "), (answer) => {
        rl.close();
        resolve(answer.trim().toLowerCase());
      });
    });

    if (confirm !== "y" && confirm !== "yes") {
      console.log(chalk.dim("\n  Cancelled.\n"));
      return;
    }

    const installConfig = readInstallConfig();
    const installMode = installConfig.installMode || "server";

    // Remove from every package manager holding a copy, not just the detected
    // one — a leftover global install elsewhere stays on PATH after an
    // "uninstalled" message.
    const holders = globalHolders(PKG_NAME);
    const pms = holders.length > 0 ? holders : [detectPackageManager()];
    const commands = pms.map((pm) => globalInstallCommand(pm, PKG_NAME, "remove"));

    for (const pm of pms) {
      if (installMode === "desktop" && packageLooksInstalled(pm, DESKTOP_PKG_NAME)) {
        commands.push(globalInstallCommand(pm, DESKTOP_PKG_NAME, "remove"));
      }
    }

    console.log(chalk.dim(`\n  Detected package manager: ${pms.join(", ")}`));
    console.log(chalk.dim(`  Install mode: ${installMode}\n`));

    try {
      for (const cmd of commands) {
        console.log(chalk.green(`  → ${cmd}\n`));
        execSync(cmd, { stdio: "inherit" });
      }
      console.log(chalk.green("\n  ✓ ThreatCrush has been uninstalled.\n"));
      console.log(chalk.dim("  We're sorry to see you go! 👋\n"));
      console.log(chalk.dim("  Config files may remain at /etc/threatcrush/"));
      console.log(chalk.dim("  Logs may remain at /var/log/threatcrush/"));
      console.log(chalk.dim("  State may remain at /var/lib/threatcrush/"));
      console.log(chalk.dim(`  Local install metadata may remain at ${INSTALL_CONFIG_PATH}\n`));
    } catch (err) {
      console.log(chalk.red("\n  ✗ Uninstall failed. Try manually:\n"));
      for (const cmd of commands) {
        console.log(chalk.dim(`    ${cmd}`));
      }
      console.log();
    }
  });

program
  .command("modules")
  .description("Manage security modules")
  .argument("[action]", "list | install | remove | available")
  .argument("[name]", "Module name or path")
  .action(async (action?: string, name?: string) => {
    await modulesCommand({ action, name });
  });

const storeCmd = program
  .command("store")
  .description("Browse the module marketplace")
  .action(async () => {
    console.log(LOGO);
    console.log(chalk.green("  Module Marketplace\n"));
    console.log(chalk.dim("  Loading available modules..."));
    try {
      const res = await fetch(`${API_URL}/api/modules`);
      if (!res.ok) {
        console.log(chalk.red(`  ✗ Failed to load modules: ${res.statusText}\n`));
        return;
      }
      const data = await res.json() as { modules?: Array<{ slug: string; display_name: string; description: string; version: string; stars?: number }> };
      const mods = data.modules || [];
      if (mods.length === 0) {
        console.log(chalk.yellow("  No modules available yet.\n"));
        return;
      }
      console.log();
      for (const mod of mods) {
        console.log(`  ${chalk.green.bold(mod.display_name || mod.slug)} ${chalk.gray(`v${mod.version}`)}${mod.stars ? chalk.gray(` ⭐${mod.stars}`) : ""}`);
        console.log(`    ${chalk.dim(mod.description)}\n`);
      }
    } catch {
      console.log(chalk.dim("  (Marketplace offline — browse at threatcrush.com/store)\n"));
    }
  });

storeCmd
  .command("search <query>")
  .description("Search for modules in the store")
  .action(async (query: string) => {
    console.log(LOGO);
    console.log(chalk.green(`  Searching for "${query}"...\n`));
    try {
      const res = await fetch(`${API_URL}/api/modules?q=${encodeURIComponent(query)}`);
      if (!res.ok) {
        console.log(chalk.red(`  ✗ Search failed: ${res.statusText}\n`));
        return;
      }
      const data = await res.json() as { modules?: Array<{ slug: string; display_name: string; description: string; version: string }> };
      const mods = data.modules || [];
      if (mods.length === 0) {
        console.log(chalk.yellow(`  No modules matching "${query}"\n`));
        return;
      }
      for (const mod of mods) {
        console.log(`  ${chalk.green.bold(mod.display_name || mod.slug)} ${chalk.gray(`v${mod.version}`)}`);
        console.log(`    ${chalk.dim(mod.description)}\n`);
      }
    } catch {
      console.log(chalk.dim(`  (Search offline — browse at threatcrush.com/store)\n`));
    }
  });

storeCmd
  .command("publish <url>")
  .description("Publish a module from a git URL or web URL")
  .action(async (url: string) => {
    console.log(LOGO);

    // 1. Get email
    const configPath = join(homedir(), ".threatcrush", "config.json");
    let email = "";
    try {
      const config = JSON.parse(readFileSync(configPath, "utf-8"));
      email = config.email || "";
    } catch {}

    if (!email) {
      const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
      email = await new Promise<string>((resolve) => {
        rl.question(chalk.green("  Enter your email: "), (answer) => {
          rl.close();
          resolve(answer.trim());
        });
      });

      if (!email || !email.includes("@")) {
        console.log(chalk.red("\n  Invalid email.\n"));
        return;
      }

      // Save email
      try {
        const dir = join(homedir(), ".threatcrush");
        if (!existsSync(dir)) mkdirSync(dir, { recursive: true });
        writeFileSync(configPath, JSON.stringify({ email }, null, 2));
        console.log(chalk.dim(`  Saved email to ${configPath}`));
      } catch {}
    }

    console.log(chalk.dim(`\n  Fetching metadata from ${url}...\n`));

    // 2. Fetch metadata
    let meta: Record<string, unknown>;
    try {
      const res = await fetch(`${API_URL}/api/modules/fetch-meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url, git_url: url }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        console.log(chalk.red(`  ✗ Failed to fetch metadata: ${(err as Record<string, string>).error}\n`));
        return;
      }
      meta = await res.json() as Record<string, unknown>;
    } catch (err) {
      console.log(chalk.red(`  ✗ Failed to fetch metadata: ${err instanceof Error ? err.message : err}\n`));
      return;
    }

    // 3. Preview
    console.log(chalk.green("  ── Module Preview ──\n"));
    console.log(`  ${chalk.bold("Name:")}         ${meta.name || chalk.dim("(none)")}`);
    console.log(`  ${chalk.bold("Display:")}      ${meta.display_name || chalk.dim("(none)")}`);
    console.log(`  ${chalk.bold("Description:")}  ${meta.description || chalk.dim("(none)")}`);
    console.log(`  ${chalk.bold("Version:")}      ${meta.version || "0.1.0"}`);
    console.log(`  ${chalk.bold("License:")}      ${meta.license || "MIT"}`);
    console.log(`  ${chalk.bold("Author:")}       ${meta.author_name || chalk.dim("(none)")}`);
    console.log(`  ${chalk.bold("Homepage:")}     ${meta.homepage_url || chalk.dim("(none)")}`);
    console.log(`  ${chalk.bold("Git:")}          ${meta.git_url || url}`);
    if (Array.isArray(meta.tags) && meta.tags.length > 0) {
      console.log(`  ${chalk.bold("Tags:")}         ${(meta.tags as string[]).join(", ")}`);
    }
    if (meta.stars) {
      console.log(`  ${chalk.bold("Stars:")}        ⭐ ${meta.stars}`);
    }
    console.log();

    // 4. Confirm
    const rl = readline.createInterface({ input: process.stdin, output: process.stdout });
    const confirm = await new Promise<string>((resolve) => {
      rl.question(chalk.yellow("  Publish this module? (y/N): "), (answer) => {
        rl.close();
        resolve(answer.trim().toLowerCase());
      });
    });

    if (confirm !== "y" && confirm !== "yes") {
      console.log(chalk.dim("\n  Cancelled.\n"));
      return;
    }

    // 5. Publish
    console.log(chalk.dim("\n  Publishing..."));
    try {
      const res = await fetch(`${API_URL}/api/modules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...meta,
          author_email: email,
          git_url: (meta.git_url as string) || url,
          homepage_url: (meta.homepage_url as string) || url,
        }),
      });

      const result = await res.json() as Record<string, unknown>;

      if (!res.ok) {
        console.log(chalk.red(`\n  ✗ Publish failed: ${(result as Record<string, string>).error}\n`));
        return;
      }

      const mod = result.module as Record<string, string>;
      const slug = mod?.slug || meta.name;
      console.log(chalk.green(`\n  ✓ Module submitted for review. It appears in the store once approved.`));
      console.log(chalk.dim(`  ${API_URL}/store/${slug}\n`));
    } catch (err) {
      console.log(chalk.red(`\n  ✗ Publish failed: ${err instanceof Error ? err.message : err}\n`));
    }
  });

// ─── Organization commands ───

const orgsCmd = program
  .command("orgs")
  .alias("org")
  .description("Manage organizations")
  .action(async () => {
    await orgsCommand({});
  });

orgsCmd
  .command("list")
  .alias("ls")
  .description("List your organizations")
  .action(async () => {
    await orgsCommand({ action: "list" });
  });

orgsCmd
  .command("create <name>")
  .description("Create a new organization")
  .action(async (name: string) => {
    await orgsCommand({ action: "create", name });
  });

orgsCmd
  .command("use <slug>")
  .description("Switch to an organization")
  .action(async (slug: string) => {
    await orgsCommand({ action: "use", name: slug });
  });

// ─── Server commands ───

const serversCmd = program
  .command("servers")
  .alias("server")
  .alias("srv")
  .description("Manage servers")
  // No subcommand lists them. Commander hands this action its options object,
  // which used to be passed as the action name: "Unknown action: [object Object]".
  .action(async () => {
    await serversCommand({ action: "list" });
  });

serversCmd
  .command("list")
  .alias("ls")
  .option("--org <slug>", "Filter by organization slug")
  .description("List servers in current organization")
  .action(async (opts) => {
    await serversCommand({ action: "list", org: opts.org });
  });

serversCmd
  .command("link")
  .option("--org <id|slug>", "Organization to link into (default: current org, or your only org)")
  .option("--name <name>", "Name for the server on the dashboard (default: hostname)")
  .description("Link this machine to the dashboard so the daemon reports to it")
  .action(async (opts: { org?: string; name?: string }) => {
    await serversCommand({ action: "link", org: opts.org, name: opts.name });
  });

serversCmd
  .command("unlink")
  .description("Stop this machine reporting to the dashboard")
  .action(async () => {
    await serversCommand({ action: "unlink" });
  });

serversCmd
  .command("restart-all")
  .option("--org <id|slug>", "Organization (default: current org, or your only org)")
  .description("Queue a daemon restart for every server you administer")
  .action(async (opts: { org?: string }) => {
    await serversCommand({ action: "restart-all", org: opts.org });
  });

// ─── Properties ───

const propsCmd = program
  .command("properties")
  .alias("props")
  .alias("targets")
  .description("Manage scan/pentest targets (properties) in the current org")
  .action(async () => {
    await propertiesListCommand({});
  });

propsCmd
  .command("list")
  .alias("ls")
  .description("List properties in the current org")
  .option("--org <slug>", "Organization slug")
  .option("--kind <kind>", "Filter by kind (url|api|domain|ip|repo)")
  .action(async (opts) => {
    await propertiesListCommand({ org: opts.org, kind: opts.kind });
  });

propsCmd
  .command("add <name> <target>")
  .description("Add a new property (url, api, domain, ip, or repo)")
  .option("--org <slug>", "Organization slug")
  .option("-k, --kind <kind>", "url | api | domain | ip | repo (auto-detected if omitted)")
  .option("-t, --tag <tags>", "Comma-separated tags")
  .option("-d, --description <text>", "Description of the property")
  .option("--disabled", "Create the property disabled")
  .action(async (name: string, target: string, opts) => {
    await propertiesAddCommand(name, target, {
      org: opts.org,
      kind: opts.kind,
      tag: opts.tag,
      description: opts.description,
      disabled: opts.disabled,
    });
  });

propsCmd
  .command("remove <nameOrId>")
  .alias("rm")
  .description("Delete a property")
  .option("--org <slug>", "Organization slug")
  .option("-y, --yes", "Skip confirmation prompt")
  .action(async (nameOrId: string, opts) => {
    await propertiesRemoveCommand(nameOrId, { org: opts.org, yes: opts.yes });
  });

propsCmd
  .command("run")
  .description("Run a scan or pentest against matching properties")
  .option("--org <slug>", "Organization slug")
  .option("-t, --type <type>", "pentest | scan (default: pentest)")
  .option("-k, --kind <kind>", "Limit to a single kind (url|api|domain|ip|repo)")
  .option("-n, --name <name>", "Run only the property with this name")
  .action(async (opts) => {
    await propertiesRunCommand({
      org: opts.org,
      type: opts.type,
      kind: opts.kind,
      name: opts.name,
    });
  });

propsCmd
  .command("runs")
  .alias("history")
  .description("Show recent scan/pentest run history")
  .option("--org <slug>", "Organization slug")
  .option("-p, --property <name>", "Show history for a single property")
  .option("-l, --limit <n>", "Max runs per property", "10")
  .action(async (opts) => {
    await propertiesRunsCommand({
      org: opts.org,
      property: opts.property,
      limit: parseInt(opts.limit, 10) || 10,
    });
  });

propsCmd
  .command("import <file>")
  .description("Bulk-import properties from a .json, .csv, or .tsv file")
  .option("--org <slug>", "Organization slug")
  .option("-n, --dry-run", "Preview without writing")
  .action(async (file: string, opts) => {
    await propertiesImportCommand(file, { org: opts.org, dryRun: opts.dryRun });
  });

// ─── Connect command ───

program
  .command("connect")
  .description("Connect to a ThreatCrush server via SSH")
  .argument("[target]", "Server name, user@hostname, or hostname")
  .option("--org <slug>", "Organization slug")
  .option("-u, --user <user>", "SSH username")
  .option("-p, --port <port>", "SSH port", "22")
  .option("-i, --identity <path>", "SSH identity file")
  .action(async (target, opts) => {
    await connectCommand({
      target,
      org: opts.org,
      user: opts.user,
      port: opts.port,
      identity: opts.identity,
    });
  });

program
  .command("help")
  .description("Show help for threatcrush or a specific command")
  .argument("[command]", "Command to show help for")
  .action((commandName?: string) => {
    if (!commandName) {
      console.log(LOGO);
      program.outputHelp();
      return;
    }
    const sub = program.commands.find(
      (c) => c.name() === commandName || c.aliases().includes(commandName),
    );
    if (!sub) {
      console.log(chalk.yellow(`  Unknown command: ${commandName}\n`));
      program.outputHelp();
      return;
    }
    sub.outputHelp();
  });

// Default action (no command — smart welcome based on current state)
program.action(() => {
  welcomeCommand();
});

program.parse();
